import {
  degrees,
  PDFDocument,
  type PDFFont,
  type PDFImage,
  type PDFPage,
  rgb,
  StandardFonts,
} from "pdf-lib"

/**
 * El PDF del recibo de flete (25/10), con la disposición del recibo de ejemplo de los
 * transportistas: membrete de la empresa de transporte, la caja con el número, a quién va
 * (SETIECA), los renglones, los totales con el exento y el IVA, el monto en letras, y abajo
 * el vehículo, el chofer, el intermediario que lo coordinó y las firmas.
 *
 * ⚠ **Se compone de la FOTO del recibo** (`snapshot`, congelada al emitir): el portal y la
 * agencia imprimen exactamente lo mismo, y corregir una ficha después no cambia un recibo.
 *
 * ⚠ **Helvetica es WinAnsi**: el español cabe entero; un carácter que no codifica (un emoji
 * pegado en una descripción) se cambia por «?» en vez de dejar el recibo sin imprimir.
 */

export type FotoRecibo = {
  numero: number
  fecha: string
  moneda: string
  tasa_bcv: string | null
  empresa: {
    razon_social: string
    rif: string
    domicilio: string | null
    telefono: string | null
    correo: string | null
    logo_documento_id: number | null
  }
  vehiculo: Partial<
    Record<"placa" | "placa_remolque" | "marca" | "modelo" | "color", string | null>
  > & {
    anio?: number | null
  }
  chofer: { nombre: string; cedula: string; telefono: string | null; licencia_grado: string | null }
  destinatario: {
    razon_social: string
    rif: string
    domicilio: string | null
    telefono?: string | null
  }
  intermediario: {
    nombre: string
    correo: string
    documento: string | null
    telefono: string | null
  }
  servicio: {
    bl: string | null
    origen: string | null
    destino: string | null
    expediente: string | null
    condiciones: string
    observaciones: string | null
  }
  lineas: {
    cantidad: string
    descripcion: string
    contenedor: string | null
    tamano: string | null
    precio_unitario: string
    importe: string
    gravado: boolean
  }[]
  totales: Record<"subtotal" | "exento" | "base_gravada" | "tasa_iva" | "iva" | "total", string>
  en_letras: string
}

const MONEDA: Record<string, string> = { BS: "Bs.", USD: "USD", EUR: "EUR" }
const TAMANO: Record<string, string> = {
  "20": "1X20",
  "40": "1X40",
  "40HC": "1X40HC",
  "45": "1X45",
  CARGA_SUELTA: "Carga suelta",
}

const cifra = (t: string | number) =>
  Number(t).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const fechaVe = (iso: string) => {
  const [a, m, d] = iso.slice(0, 10).split("-")
  return `${d}/${m}/${a}`
}

export async function pdfRecibo(
  f: FotoRecibo,
  logo: Uint8Array | null,
  logoMime: string | null,
  anulado = false,
): Promise<Uint8Array> {
  const doc = await PDFDocument.create()
  doc.setTitle(`Recibo ${String(f.numero).padStart(6, "0")} · ${f.empresa.razon_social}`)
  const pagina = doc.addPage([612, 792])
  const normal = await doc.embedFont(StandardFonts.Helvetica)
  const negrita = await doc.embedFont(StandardFonts.HelveticaBold)
  const negro = rgb(0, 0, 0)
  const gris = rgb(0.35, 0.35, 0.35)

  const limpio = (t: string, fuente: PDFFont) =>
    [...t]
      .map((c) => {
        try {
          fuente.encodeText(c)
          return c
        } catch {
          return "?"
        }
      })
      .join("")
  const texto = (t: string, x: number, y: number, tam = 9, fuente = normal, color = negro) =>
    pagina.drawText(limpio(t, fuente), { x, y, size: tam, font: fuente, color })
  const derecha = (t: string, xDer: number, y: number, tam = 9, fuente = normal) =>
    texto(t, xDer - fuente.widthOfTextAtSize(limpio(t, fuente), tam), y, tam, fuente)
  const caja = (x: number, y: number, w: number, h: number, grosor = 0.8) =>
    pagina.drawRectangle({ x, y, width: w, height: h, borderColor: negro, borderWidth: grosor })
  const linea = (x1: number, y1: number, x2: number, y2: number, grosor = 0.8) =>
    pagina.drawLine({
      start: { x: x1, y: y1 },
      end: { x: x2, y: y2 },
      thickness: grosor,
      color: negro,
    })
  const partir = (t: string, ancho: number, tam: number, fuente = normal) => {
    const salida: string[] = []
    let actual = ""
    for (const p of limpio(t, fuente).split(/\s+/)) {
      const prueba = actual ? `${actual} ${p}` : p
      if (fuente.widthOfTextAtSize(prueba, tam) > ancho && actual) {
        salida.push(actual)
        actual = p
      } else actual = prueba
    }
    if (actual) salida.push(actual)
    return salida
  }

  // Membrete: el logo (o la razón social) a la izquierda, el RIF grande a la derecha.
  let imagen: PDFImage | null = null
  if (logo && logoMime) {
    try {
      imagen = logoMime.includes("png") ? await doc.embedPng(logo) : await doc.embedJpg(logo)
    } catch {
      imagen = null
    }
  }
  if (imagen) {
    const escala = Math.min(170 / imagen.width, 80 / imagen.height)
    pagina.drawImage(imagen, {
      x: 50,
      y: 690,
      width: imagen.width * escala,
      height: imagen.height * escala,
    })
  } else {
    partir(f.empresa.razon_social, 280, 16, negrita).forEach((l, i) => {
      texto(l, 50, 740 - i * 18, 16, negrita)
    })
  }
  // Con logo, la razón social va arriba a la derecha: el logo no siempre la lleva escrita.
  let yd = 748
  if (imagen) {
    for (const l of partir(f.empresa.razon_social, 300, 12, negrita).slice(0, 2)) {
      derecha(l, 562, yd, 12, negrita)
      yd -= 14
    }
    yd -= 4
  }
  derecha(f.empresa.rif, 562, yd - 8, imagen ? 14 : 18, negrita)
  yd -= imagen ? 22 : 26
  if (f.empresa.domicilio) {
    for (const l of partir(f.empresa.domicilio, 250, 7).slice(0, 3)) {
      derecha(l, 562, yd, 7)
      yd -= 9
    }
  }
  if (f.empresa.telefono) derecha(`Tel.: ${f.empresa.telefono}`, 562, yd - 2, 7)
  derecha(
    `${f.servicio.origen ? `${f.servicio.origen.split(/[-,]/)[0]?.trim()} ` : ""}${fechaVe(f.fecha)}`,
    562,
    672,
    10,
  )

  // La caja del encabezado.
  const xi = 50
  const xd = 562
  const xm = 360
  const xn = 470
  let y = 655
  // ⚠ Siete filas: 17 + 6 × 15 = 107 de alto, más los 15 de la última. Con 136 el borde
  // de abajo caía dentro de la cabecera de la tabla y tachaba «Cant.», «DESCRIPCIÓN»…
  caja(xi, y - 122, xd - xi, 122)
  const fila = (alto: number) => {
    y -= alto
    linea(xi, y, xd, y, 0.5)
  }
  texto("RECIBO Nº", xm + 4, y - 12, 10, negrita)
  derecha(String(f.numero).padStart(6, "0"), xd - 30, y - 12, 10)
  linea(xm, y, xm, y - 17)
  linea(xn, y, xn, y - 17)
  fila(17)
  texto("Nombre o Razón Social:", xi + 4, y - 11, 9, negrita)
  texto("Fecha:", xm + 4, y - 11, 9, negrita)
  fila(15)
  texto(f.destinatario.razon_social, xi + 4, y - 11)
  texto(fechaVe(f.fecha), xm + 4, y - 11)
  linea(xm, y + 15, xm, y - 15, 0.5)
  fila(15)
  texto("Domicilio Fiscal:", xi + 4, y - 11, 9, negrita)
  const domicilio = partir(f.destinatario.domicilio ?? "", 400, 8)
  texto(domicilio[0] ?? "", xi + 82, y - 11, 8)
  fila(15)
  texto(domicilio.slice(1).join(" ").slice(0, 110), xi + 4, y - 11, 8)
  fila(15)
  texto("Teléfono:", xi + 4, y - 11, 9, negrita)
  texto(f.destinatario.telefono ?? "", xi + 52, y - 11, 8)
  texto("RIF:", xm + 4, y - 11, 9, negrita)
  linea(xm, y + 15, xm, y - 30, 0.5)
  fila(15)
  texto(f.destinatario.rif, xm + 4, y - 11)
  fila(15)
  texto("Condiciones de Pago:", xi + 4, y - 11, 9, negrita)
  texto(f.servicio.condiciones, xi + 100, y - 11)
  if (f.servicio.expediente)
    texto(`Expediente: ${f.servicio.expediente}`, xm + 4, y - 11, 8, negrita)
  y -= 22

  // Los renglones.
  const mon = MONEDA[f.moneda] ?? f.moneda
  const [c0, c1, c2, c3, c4] = [xi, xi + 40, 380, 470, xd]
  const alto = 230
  caja(xi, y - alto, xd - xi, alto)
  caja(xi, y - 16, xd - xi, 16)
  for (const c of [c1, c2, c3]) linea(c, y, c, y - alto)
  texto("Cant.", c0 + 8, y - 11, 9, negrita)
  texto("DESCRIPCIÓN", 170, y - 11, 9, negrita)
  texto(`P. Unitario ${mon}`, c2 + 10, y - 11, 9, negrita)
  texto(`Total ${mon}`, c3 + 20, y - 11, 9, negrita)
  let yl = y - 30
  for (const l of f.lineas) {
    if (yl < y - alto + 40) break
    derecha(cifra(l.cantidad).replace(/,00$/, ""), c1 - 10, yl, 9)
    const desc = partir(l.descripcion + (l.gravado ? " (G)" : ""), c2 - c1 - 10, 9)
    desc.forEach((d, i) => {
      texto(d, c1 + 4, yl - i * 11)
    })
    derecha(cifra(l.precio_unitario), c3 - 6, yl, 9, negrita)
    derecha(cifra(l.importe), c4 - 6, yl, 9, negrita)
    yl -= desc.length * 11
    if (l.contenedor || l.tamano) {
      texto(
        `CONTENEDOR: ${l.contenedor ?? "—"}  ${l.tamano ? (TAMANO[l.tamano] ?? l.tamano) : ""}`,
        c1 + 4,
        yl,
        8,
        negrita,
      )
      yl -= 11
    }
    yl -= 6
  }
  const extras = [
    f.servicio.origen || f.servicio.destino
      ? `${f.servicio.origen ?? ""} - ${f.servicio.destino ?? ""}`
      : "",
    f.servicio.bl ? `BL: ${f.servicio.bl}` : "",
  ].filter(Boolean)
  extras.forEach((t, i) => {
    texto(t, c1 + 4, yl - 8 - i * 12, 9, negrita)
  })
  y -= alto

  // Totales.
  const t = f.totales
  const filasTot: [string, string][] = [
    [`Sub - Total ${mon}`, cifra(t.subtotal)],
    [`EXENTO ${mon}`, cifra(t.exento)],
    [
      `I.V.A. ${cifra(t.tasa_iva).replace(/,00$/, "")} % sobre ${cifra(t.base_gravada)}`,
      cifra(t.iva),
    ],
    [`Total a Pagar ${mon}`, cifra(t.total)],
  ]
  for (const [rotulo, valor] of filasTot) {
    caja(c1, y - 15, c4 - c1, 15, 0.8)
    linea(c3, y, c3, y - 15)
    derecha(rotulo, c3 - 6, y - 11, 9, negrita)
    derecha(valor, c4 - 6, y - 11, 9, negrita)
    y -= 15
  }
  y -= 18
  texto("SON:", xi, y, 9, negrita)
  partir(f.en_letras, 450, 9, negrita).forEach((l, i) => {
    texto(l, xi + 40, y - i * 12, 9, negrita)
  })
  y -= 30
  if (f.moneda !== "BS" && f.tasa_bcv) {
    texto(
      `Tasa BCV de referencia al emitir: ${cifra(f.tasa_bcv)} Bs. por USD`,
      xi,
      y,
      8,
      normal,
      gris,
    )
    y -= 14
  }

  // El servicio: vehículo, chofer e intermediario.
  y -= 6
  caja(xi, y - 62, xd - xi, 62, 0.5)
  const v = f.vehiculo
  texto("VEHÍCULO", xi + 6, y - 12, 8, negrita)
  texto(
    `Placa: ${v.placa ?? "—"}${v.placa_remolque ? `   Remolque: ${v.placa_remolque}` : ""}`,
    xi + 6,
    y - 24,
    8,
  )
  texto([v.marca, v.modelo, v.color, v.anio].filter(Boolean).join(" · "), xi + 6, y - 35, 8)
  texto("CHOFER", 230, y - 12, 8, negrita)
  texto(f.chofer.nombre, 230, y - 24, 8)
  texto(
    `C.I. ${f.chofer.cedula}${f.chofer.licencia_grado ? `   Licencia ${f.chofer.licencia_grado}` : ""}`,
    230,
    y - 35,
    8,
  )
  if (f.chofer.telefono) texto(`Tel. ${f.chofer.telefono}`, 230, y - 46, 8)
  texto("COORDINADO POR", 410, y - 12, 8, negrita)
  texto(f.intermediario.nombre, 410, y - 24, 8)
  texto(
    [f.intermediario.documento, f.intermediario.telefono].filter(Boolean).join(" · "),
    410,
    y - 35,
    8,
  )
  texto(f.intermediario.correo, 410, y - 46, 7)
  if (f.servicio.observaciones) {
    partir(`Obs.: ${f.servicio.observaciones}`, 500, 7)
      .slice(0, 2)
      .forEach((l, i) => {
        texto(l, xi + 6, y - 56 + i * -9, 7, normal, gris)
      })
  }
  y -= 110

  // Firmas.
  linea(xi + 20, y, xi + 220, y, 0.6)
  linea(xd - 220, y, xd - 20, y, 0.6)
  texto("Entregado por (transportista)", xi + 55, y - 12, 8)
  texto("Recibido por (SETIECA)", xd - 170, y - 12, 8)

  if (anulado) marcaAnulado(pagina, negrita)
  return doc.save()
}

function marcaAnulado(pagina: PDFPage, fuente: PDFFont) {
  pagina.drawText("ANULADO", {
    x: 140,
    y: 360,
    size: 90,
    font: fuente,
    color: rgb(0.8, 0.1, 0.1),
    opacity: 0.25,
    rotate: degrees(30),
  })
}
